import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMoney } from "@/lib/currency";
import { queryOptions, useSuspenseQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Suspense } from "react";

import { getCart, removeCartItem, clearCart } from "@/lib/cart.functions";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const cartQuery = queryOptions({
  queryKey: ["cart"],
  queryFn: () => getCart(),
  staleTime: 0,
});

export const Route = createFileRoute("/cart")({
  head: () => ({ meta: [{ title: "Carrito — Mostrando México" }, { name: "robots", content: "noindex,nofollow" }] }),
  loader: ({ context }) => context.queryClient.ensureQueryData(cartQuery),
  component: CartPage,
});

function CartPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <header className="border-b">
        <div className="mx-auto max-w-3xl px-6 py-4">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
            ← Seguir explorando
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-6 py-8">
        <h1 className="mb-6 text-2xl font-semibold">Tu carrito</h1>
        <Suspense fallback={<Skeleton className="h-48 w-full" />}>
          <CartBody />
        </Suspense>
      </main>
      <SiteFooter />
    </div>
  );
}

function CartBody() {
  const money = useMoney();
  const { data: cart } = useSuspenseQuery(cartQuery);
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: (id: string) => removeCartItem({ data: { id } }),
    onSuccess: (c) => qc.setQueryData(["cart"], c),
  });
  const clr = useMutation({
    mutationFn: () => clearCart(),
    onSuccess: (c) => qc.setQueryData(["cart"], c),
  });

  if (!cart.items.length) {
    return (
      <div className="rounded-lg border bg-card p-8 text-center">
        <p className="text-muted-foreground">Tu carrito está vacío.</p>
        <Link to="/" className="mt-4 inline-block text-sm font-medium underline">
          Ver productos
        </Link>
      </div>
    );
  }

  const total = cart.items.reduce((s, i) => s + (i.unitTotal ?? 0), 0);
  const currency = cart.items[0]?.currency ?? "USD";

  return (
    <div className="space-y-4">
      {cart.items.map((item) => {
        const pax = item.pricingCategoryBookings.reduce((s, b) => s + b.quantity, 0);
        return (
          <div key={item.id} className="flex gap-4 rounded-lg border bg-card p-4">
            {item.image && (
              <img loading="lazy" decoding="async" src={item.image} alt="" className="h-20 w-20 rounded-md object-cover" />
            )}
            <div className="flex-1">
              <h3 className="font-medium">{item.title}</h3>
              <p className="text-sm text-muted-foreground">
                {item.date}
                {item.startTime ? ` · ${item.startTime}` : ""} · {pax} pax
              </p>
              <p className="mt-1 text-sm font-semibold">
                {money(item.unitTotal ?? 0, item.currency)}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => remove.mutate(item.id)}>
              Quitar
            </Button>
          </div>
        );
      })}

      <div className="flex items-center justify-between rounded-lg bg-muted/40 p-4">
        <span className="text-sm">Total</span>
        <span className="text-lg font-semibold">
          {money(total, currency)}
        </span>
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={() => clr.mutate()} disabled={clr.isPending}>
          Vaciar
        </Button>
        <Link to="/checkout" className="ml-auto">
          <Button>Ir al checkout</Button>
        </Link>
      </div>
    </div>
  );
}
